/**
 * Disable a multi-page Gravity Forms step's Continue / Submit buttons (and
 * Buy Now) until every required field on that step is filled in.
 *
 * Follows the "Required" setting in Gravity Forms: any field with
 * `.gfield_contains_required` counts, unless conditional logic hides it.
 * The airport search step also needs `.airport-counter` at 1 or more
 * (searched or unlisted airports), and the `.terms-consent` Checkbox field
 * needs every box ticked. Required fields are marked by Gravity Forms' own
 * asterisk.
 * Go Back is never disabled. GF still validates on the server, so this is
 * only a guide for the visitor.
 */

const FOOTER_BUTTONS = '.gform_next_button, .gform_button, .buy-now-button';

const isHidden = (field) => window.getComputedStyle(field).display === 'none';

const hasValue = (el) => el.value.trim() !== '';

/**
 * Whether one required field has an answer.
 */
const isFilled = (field) => {
  // Airport search: the text box is only for searching; picks are the boxes.
  if (field.querySelector('.airport-search')) {
    return !!field.querySelector('.airport-search__item');
  }

  const all = [...field.querySelectorAll('input, select, textarea')]
    .filter((el) => !['hidden', 'button', 'submit'].includes(el.type));

  // Radio / checkbox / consent: something ticked. Disabled ones still count
  // as unanswered (e.g. consent locked until the EULA is read, eula.js).
  const choices = all.filter((el) => el.type === 'radio' || el.type === 'checkbox');

  // EULA agreement: every box, not just one (GF's Required only needs one;
  // lib/gravity-forms.php checks the same on submit).
  if (choices.length && field.classList.contains('terms-consent')) {
    return choices.every((el) => el.checked && !el.disabled);
  }

  if (choices.length) {
    return choices.some((el) => el.checked && !el.disabled);
  }

  const inputs = all.filter((el) => !el.disabled);

  // List: any cell in any row.
  if (field.classList.contains('gfield--type-list')) {
    return inputs.some(hasValue);
  }

  // Name, Address, etc. mark just their required parts with aria-required.
  const required = inputs.filter((el) => el.getAttribute('aria-required') === 'true');
  return (required.length ? required : inputs).every(hasValue);
};

/**
 * Whether at least one airport is picked or listed as unlisted, from the
 * `.airport-counter` field airport-search.js keeps up to date.
 */
const hasAirport = (form) => {
  const counter = form.querySelector('.airport-counter input');
  return !!counter && Number(counter.value) >= 1;
};

/**
 * Enable / disable each page's forward buttons from its required fields.
 * The step with the airport search also needs at least one airport.
 */
const updateButtons = (form) => {
  if (!form) {
    return;
  }

  // Multi-page forms have a .gform_page per step; single-page forms don't.
  const pages = form.querySelectorAll('.gform_page');
  (pages.length ? [...pages] : [form]).forEach((page) => {
    const complete = [...page.querySelectorAll('.gfield.gfield_contains_required')]
      .every((field) => isHidden(field) || isFilled(field))
      && (!page.querySelector('.airport-search') || hasAirport(form));

    page.querySelectorAll(FOOTER_BUTTONS).forEach((button) => {
      if (!button.classList.contains('gform_previous_button')) {
        button.disabled = !complete;
      }
    });
  });
};

const updateAll = () => {
  document.querySelectorAll('.airport-search').forEach((container) => updateButtons(container.closest('form')));
};

document.addEventListener('DOMContentLoaded', updateAll);

// Typing, ticking, and airport picks (airport-search.js fires change on the
// counter fields when a box is added or removed).
['input', 'change'].forEach((type) => {
  document.addEventListener(type, (event) => {
    if (event.target.form && event.target.form.querySelector('.airport-search')) {
      updateButtons(event.target.form);
    }
  });
});

if (window.jQuery) {
  window.jQuery(document).on('gform_post_render', updateAll);

  // Fields shown / hidden by conditional logic change what's required.
  window.jQuery(document).on('gform_post_conditional_logic', (event, formId) => {
    updateButtons(document.getElementById(`gform_${formId}`));
  });
}
